import React, { useState, useEffect } from 'react';
import { arcgisAPI } from '../../lib/apiRoutes';
import { toast } from 'sonner';
import ArcGISFeatureMap from '../../components/ArcGISFeatureMap';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const FEATURE_SERVICE_URL = "https://services2.arcgis.com/w0DLXtWY5Mf6iZvz/arcgis/rest/services/service_da5ef023332a450c98922c2afc453ce5/FeatureServer";

const AdminMapa = () => {
  const [arcgisToken, setArcgisToken] = useState(null);
  const [tokenLoading, setTokenLoading] = useState(true);
  const [whereClause, setWhereClause] = useState("1=1");
  const [refreshKey, setRefreshKey] = useState(0);
  const [activeQuickFilter, setActiveQuickFilter] = useState('default'); // Track active quick filter
  const [filters, setFilters] = useState({
    estado: '',
    lugar: '',
    fechaDesde: '',
    fechaHasta: ''
  });

  // Load ArcGIS token on mount
  useEffect(() => {
    loadToken();
  }, []);

  const loadToken = async () => {
    setTokenLoading(true);
    try {
      const response = await arcgisAPI.getToken();
      if (response.data.success && response.data.token) {
        setArcgisToken(response.data.token);
      }
    } catch (error) {
      console.error('Error loading ArcGIS token:', error);
      toast.error('Error cargando token de ArcGIS');
    } finally {
      setTokenLoading(false);
    }
  };

  // Default date range: 5 days back to 60 days forward
  useEffect(() => {
    const today = new Date();
    const fiveDaysAgo = new Date(today);
    fiveDaysAgo.setDate(today.getDate() - 5);
    const sixtyDaysForward = new Date(today);
    sixtyDaysForward.setDate(today.getDate() + 60);
    
    setFilters(prev => ({
      ...prev,
      fechaDesde: fiveDaysAgo.toISOString().split('T')[0],
      fechaHasta: sixtyDaysForward.toISOString().split('T')[0]
    }));
  }, []);

  // Build where clause when filters change
  useEffect(() => {
    buildWhereClause();
  }, [filters]);

  const buildWhereClause = () => {
    const clauses = [];
    
    if (filters.estado) {
      clauses.push(`Estado = '${filters.estado}'`);
    }
    
    if (filters.lugar) {
      clauses.push(`Indique_si_intra_o_extramuros LIKE '%${filters.lugar}%'`);
    }
    
    // Date filters
    if (filters.fechaDesde) {
      const desde = new Date(filters.fechaDesde);
      clauses.push(`EditDate >= timestamp '${desde.toISOString().slice(0, 10)} 00:00:00'`);
    }
    
    if (filters.fechaHasta) {
      const hasta = new Date(filters.fechaHasta);
      clauses.push(`EditDate <= timestamp '${hasta.toISOString().slice(0, 10)} 23:59:59'`);
    }
    
    const newWhere = clauses.length > 0 ? clauses.join(' AND ') : '1=1';
    setWhereClause(newWhere);
  };

  const applyFilters = () => {
    buildWhereClause();
    setRefreshKey(prev => prev + 1);
    toast.success('Filtros aplicados');
  };

  const setQuickDateRange = (daysBack, daysForward, filterId) => {
    const today = new Date();
    const desde = new Date(today);
    desde.setDate(today.getDate() - daysBack);
    const hasta = new Date(today);
    hasta.setDate(today.getDate() + daysForward);
    
    setFilters(prev => ({
      ...prev,
      fechaDesde: desde.toISOString().split('T')[0],
      fechaHasta: hasta.toISOString().split('T')[0]
    }));
    setActiveQuickFilter(filterId);
  };

  const zonas = ['Intramuros', 'Extramuros', 'Puerta Tierra'];
  const estados = ['Pendiente', 'Notificada', 'Asignada', 'Realizada', 'Cancelada'];

  return (
    <div className="space-y-6" data-testid="admin-mapa-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Mapa de Recogidas</h1>
          <p className="text-sm text-gray-500 mt-1">
            Capa en tiempo real desde ArcGIS Online - Cádiz
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={loadToken} variant="outline" size="sm">
            🔑 Renovar Token
          </Button>
          <Button onClick={() => setRefreshKey(prev => prev + 1)} variant="outline">
            🔄 Recargar Capa
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <span>🔍</span> Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Quick date buttons */}
            <div className="flex flex-wrap gap-2 pb-3 border-b">
              <span className="text-sm text-gray-500 mr-2">Rango rápido:</span>
              <Button 
                variant={activeQuickFilter === 'today' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setQuickDateRange(0, 0, 'today')}
              >
                Hoy
              </Button>
              <Button 
                variant={activeQuickFilter === 'default' ? 'default' : 'outline'}
                size="sm"
                className={activeQuickFilter === 'default' ? '' : 'bg-teal-50 border-teal-200 hover:bg-teal-100'}
                onClick={() => setQuickDateRange(5, 60, 'default')}
              >
                Por defecto (-5 a +60 días)
              </Button>
              <Button 
                variant={activeQuickFilter === 'next7' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setQuickDateRange(0, 7, 'next7')}
              >
                Próximos 7 días
              </Button>
              <Button 
                variant={activeQuickFilter === 'last30' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setQuickDateRange(30, 0, 'last30')}
              >
                Últimos 30 días
              </Button>
              <Button 
                variant={activeQuickFilter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  setFilters(prev => ({
                    ...prev,
                    fechaDesde: '2020-01-01',
                    fechaHasta: '2030-12-31'
                  }));
                  setActiveQuickFilter('all');
                }}
              >
                Todo el histórico
              </Button>
            </div>

            <div className="grid grid-cols-4 gap-4">
              <div>
                <Label>Fecha Desde</Label>
                <Input
                  type="date"
                  value={filters.fechaDesde}
                  onChange={(e) => setFilters({ ...filters, fechaDesde: e.target.value })}
                />
              </div>
              <div>
                <Label>Fecha Hasta</Label>
                <Input
                  type="date"
                  value={filters.fechaHasta}
                  onChange={(e) => setFilters({ ...filters, fechaHasta: e.target.value })}
                />
              </div>
              <div>
                <Label>Estado</Label>
                <select
                  value={filters.estado}
                  onChange={(e) => setFilters({ ...filters, estado: e.target.value })}
                  className="w-full p-2 border rounded-md text-sm"
                >
                  <option value="">Todos los estados</option>
                  {estados.map(e => (
                    <option key={e} value={e}>{e}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Zona</Label>
                <select
                  value={filters.lugar}
                  onChange={(e) => setFilters({ ...filters, lugar: e.target.value })}
                  className="w-full p-2 border rounded-md text-sm"
                >
                  <option value="">Todas las zonas</option>
                  {zonas.map(z => (
                    <option key={z} value={z}>{z}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-500">
                Filtro: <code className="bg-gray-100 px-2 py-1 rounded text-xs">{whereClause.substring(0, 80)}{whereClause.length > 80 ? '...' : ''}</code>
              </p>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    const today = new Date();
                    const fiveDaysAgo = new Date(today);
                    fiveDaysAgo.setDate(today.getDate() - 5);
                    const sixtyDaysForward = new Date(today);
                    sixtyDaysForward.setDate(today.getDate() + 60);
                    
                    setFilters({
                      estado: '',
                      lugar: '',
                      fechaDesde: fiveDaysAgo.toISOString().split('T')[0],
                      fechaHasta: sixtyDaysForward.toISOString().split('T')[0]
                    });
                    setActiveQuickFilter('default');
                  }}
                >
                  Limpiar filtros
                </Button>
                <Button onClick={applyFilters}>
                  Aplicar Filtros
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Map */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center justify-between">
            <span>
              📍 Capa de ArcGIS - Cádiz
              <span className="text-sm font-normal text-gray-500 ml-2">
                (Haz clic en un punto para ver detalles)
              </span>
            </span>
            {arcgisToken && (
              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                ✓ Token activo
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {tokenLoading ? (
            <div className="h-[550px] flex items-center justify-center bg-gray-100 rounded-lg">
              <div className="text-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-3"></div>
                <p className="text-gray-600">Obteniendo token de ArcGIS...</p>
              </div>
            </div>
          ) : (
            <ArcGISFeatureMap 
              key={refreshKey}
              featureServiceUrl={FEATURE_SERVICE_URL}
              layerId={0}
              whereClause={whereClause}
              height="550px"
              token={arcgisToken}
            />
          )}
        </CardContent>
      </Card>

      {/* Info */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="py-4">
          <p className="text-sm text-blue-800">
            <strong>ℹ️ Información:</strong> Este mapa carga datos directamente desde la capa de ArcGIS Online.
            Los puntos se muestran según el estado (color) y puedes hacer clic para ver los detalles de cada recogida.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminMapa;
